import { FormEvent, useEffect, useState } from "react";
import { CalendarCheck, LoaderCircle, ShieldCheck } from "lucide-react";

import { ApiError, convertBooking, getWorkforce } from "./api";
import { localBusinessTimeToIso } from "./availability-time";
import type { BookingRequestDetail, SchedulingConflict, Team } from "./types";

export function BookingConversionForm({
  booking,
  canWrite,
  onConverted,
}: {
  booking: BookingRequestDetail;
  canWrite: boolean;
  onConverted: () => Promise<void>;
}) {
  const [teams, setTeams] = useState<Team[]>([]);
  const [loading, setLoading] = useState(true);
  const [sending, setSending] = useState(false);
  const [errorCode, setErrorCode] = useState<string>();
  const [conflicts, setConflicts] = useState<SchedulingConflict[]>([]);

  useEffect(() => {
    let active = true;
    void getWorkforce()
      .then((overview) => active && setTeams(overview.teams.filter((team) => team.active)))
      .catch(() => active && setErrorCode("WORKFORCE_LOAD_FAILED"))
      .finally(() => active && setLoading(false));
    return () => {
      active = false;
    };
  }, []);

  async function submit(event: FormEvent<HTMLFormElement>) {
    event.preventDefault();
    setSending(true);
    setErrorCode(undefined);
    setConflicts([]);
    const fields = new FormData(event.currentTarget);
    try {
      await convertBooking(booking.id, {
        teamId: String(fields.get("teamId")),
        startAt: localBusinessTimeToIso(
          `${String(fields.get("appointmentDate"))}T${String(fields.get("appointmentTime"))}`,
        ),
        status: String(fields.get("appointmentStatus")) as "TENTATIVE" | "CONFIRMED",
        internalNotes: nullable(fields.get("appointmentNotes")),
        expectedUpdatedAt: booking.updatedAt,
      });
      await onConverted();
    } catch (error) {
      const apiError = error instanceof ApiError ? error : undefined;
      setErrorCode(apiError?.code ?? "REQUEST_FAILED");
      setConflicts(apiError?.conflicts ?? []);
      setSending(false);
    }
  }

  if (booking.status === "SCHEDULED") {
    return (
      <section className="conversion-card conversion-complete">
        <CalendarCheck />
        <div>
          <strong>Appointment created</strong>
          <span>This request is already linked to its protected schedule record.</span>
        </div>
      </section>
    );
  }

  const missingLink = !booking.clientId || !booking.propertyId;
  return (
    <form className="conversion-card" onSubmit={submit}>
      <header>
        <div className="conversion-icon">
          <ShieldCheck />
        </div>
        <div>
          <span className="eyebrow lime">Protected scheduling</span>
          <h3>Create appointment</h3>
          <p>
            Select the actual team and start time. Duration, working hours, time off, travel, and
            overlaps are checked before anything is reserved.
          </p>
        </div>
      </header>
      {missingLink ? (
        <p className="conversion-prerequisite" role="status">
          Link both a client and property, then save the request before scheduling.
        </p>
      ) : (
        <div className="conversion-grid">
          <label>
            Team
            <select name="teamId" required disabled={loading || !teams.length}>
              <option value="">{loading ? "Loading teams…" : "Select team"}</option>
              {teams.map((team) => (
                <option key={team.id} value={team.id}>
                  {team.name}
                </option>
              ))}
            </select>
          </label>
          <label>
            Date
            <input
              name="appointmentDate"
              type="date"
              defaultValue={booking.preferredDate ?? ""}
              required
            />
          </label>
          <label>
            Start time
            <input
              name="appointmentTime"
              type="time"
              defaultValue={booking.preferredWindowStart?.slice(0, 5) ?? ""}
              required
            />
          </label>
          <label>
            Initial status
            <select name="appointmentStatus" defaultValue="CONFIRMED">
              <option value="CONFIRMED">Confirmed</option>
              <option value="TENTATIVE">Tentative</option>
            </select>
          </label>
          <label className="wide-field">
            Internal scheduling note
            <textarea name="appointmentNotes" rows={2} maxLength={10000} />
          </label>
        </div>
      )}
      {errorCode ? <ConversionError code={errorCode} conflicts={conflicts} /> : null}
      {!canWrite ? <p className="form-error">Your role has read-only access.</p> : null}
      {!missingLink ? (
        <button
          className="primary-button compact-action conversion-submit"
          disabled={sending || loading || !canWrite || teams.length === 0}
        >
          {sending ? <LoaderCircle className="spin" /> : <CalendarCheck />} Run checks & create
        </button>
      ) : null}
    </form>
  );
}

function ConversionError({ code, conflicts }: { code: string; conflicts: SchedulingConflict[] }) {
  return (
    <div className="conversion-error" role="alert">
      <strong>{errorTitle(code)}</strong>
      {conflicts.length ? (
        <ul>
          {conflicts.map((conflict) => (
            <li key={`${conflict.code}-${conflict.entityId ?? "general"}`}>
              {conflict.message}
              {conflict.requiredMinutes ? ` (${conflict.requiredMinutes} minutes required)` : ""}
            </li>
          ))}
        </ul>
      ) : (
        <span>Refresh the request, verify its links, and try again.</span>
      )}
    </div>
  );
}

function errorTitle(code: string) {
  if (code === "BOOKING_STALE") return "This request changed in another session.";
  if (code === "BOOKING_CLIENT_REQUIRED") return "A linked client is required.";
  if (code === "BOOKING_PROPERTY_REQUIRED") return "A linked property is required.";
  if (code === "SCHEDULE_CONFLICTS") return "This slot cannot be reserved yet.";
  return "We couldn’t create the appointment.";
}

function nullable(value: FormDataEntryValue | null) {
  const text = String(value ?? "").trim();
  return text || null;
}
